import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

interface ProfitTrendData {
  date: string;
  profit: number;
  cumulativeProfit: number;
}

interface ProfitTrendChartProps {
  data: ProfitTrendData[];
}

const ProfitTrendChart: React.FC<ProfitTrendChartProps> = ({ data }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1e293b' }}>
            {new Date(data.date).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            })}
          </p>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '4px' }}>
            Daily: {formatCurrency(data.profit)}
          </p>
          <p style={{
            color: data.cumulativeProfit >= 0 ? '#10b981' : '#ef4444',
            fontWeight: 600,
            fontSize: '0.95rem'
          }}>
            Total: {formatCurrency(data.cumulativeProfit)}
          </p>
        </div>
      );
    }
    return null;
  };

  const minProfit = Math.min(...data.map(d => d.cumulativeProfit));
  const maxProfit = Math.max(...data.map(d => d.cumulativeProfit));
  const profitRange = maxProfit - minProfit;
  const yAxisMin = minProfit - (profitRange * 0.1);
  const yAxisMax = maxProfit + (profitRange * 0.1);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
        <defs>
          <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tickFormatter={formatDate}
          tick={{ fontSize: 12 }}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12 }}
          domain={[yAxisMin, yAxisMax]}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="cumulativeProfit"
          stroke="#10b981"
          strokeWidth={2}
          fill="url(#profitGradient)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default ProfitTrendChart;
